import { useCallback, useEffect, useRef, useState } from 'react';
import { getMediaDetails, getSeasonEpisodes } from '@/services/mediaApi';
import type { MediaDetails, EpisodeSummary, MediaType } from '@/types/streaming';

interface UseMediaArgs {
  type: MediaType;
  tmdbId: number;
}

interface UseMediaState {
  details: MediaDetails | null;
  episodes: Record<number, EpisodeSummary[]>;
  loading: boolean;
  error: string | null;
  loadSeason: (season: number) => Promise<void>;
}

export function useMedia({ type, tmdbId }: UseMediaArgs): UseMediaState {
  const [details, setDetails] = useState<MediaDetails | null>(null);
  const [episodes, setEpisodes] = useState<Record<number, EpisodeSummary[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether this hook is still mounted with the current (type, tmdbId).
  // Bumped on unmount AND on tmdbId change so an in-flight `loadSeason` from
  // a previous show can't update state belonging to a new show.
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const myId = requestIdRef.current;
    setLoading(true);
    setError(null);
    getMediaDetails(type, tmdbId)
      .then((d) => {
        if (requestIdRef.current !== myId) return;
        setDetails(d);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (requestIdRef.current !== myId) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { requestIdRef.current += 1; };
  }, [type, tmdbId]);

  const loadSeason = useCallback(async (season: number) => {
    const myId = requestIdRef.current;
    try {
      const eps = await getSeasonEpisodes(tmdbId, season);
      // Drop the result if the component unmounted or the user switched
      // shows while this fetch was in flight — otherwise React logs
      // "state update on unmounted component" and stale episodes leak
      // into the next show.
      if (requestIdRef.current !== myId) return;
      setEpisodes((prev) => ({ ...prev, [season]: eps }));
    } catch (err) {
      if (requestIdRef.current !== myId) return;
      // Surface in the same channel `getMediaDetails` errors use so callers
      // can show a single error UI; the previous code let rejections become
      // unhandled promise errors and the season list stayed in loading.
      setError(err instanceof Error ? err.message : 'Failed to load season');
    }
  }, [tmdbId]);

  return { details, episodes, loading, error, loadSeason };
}
